import { useState } from 'react';
import { toast } from '@cencosud-cencommerce/paris-uikit';

import { useAppSelector } from '@/Application/Hooks/Redux';
import { useRepository } from '@/Application/Providers/Repository';
import { useCart } from '@/Application/Selectors/cart/useCart';
import selectShippingMethod from '@/modules/cart/application/selectShippingMethod/selectShippingMethod';
import type { Cart } from '@/modules/cart/domain/Cart';
import {
  hasCollectShippingOption,
  refreshOnBehalfGroups
} from '@/modules/cart/infrastructure/CartReduxRepository/slices/CartSlice/utils';
import type {
  DeliveryGroup,
  PickupOption,
  ShippingItem,
  ShippingMethod,
  ShippingOption
} from '@/modules/shippingMethod/domain/ShippingMethod';
import type {
  GeneralCollectOptionDto,
  GeneralCollectShippingOptionDto,
  GeneralDeliveryShippingOptionDto,
  HttpShippingMethodDraft,
  HttpShippingMethodDto
} from '@/modules/shippingMethod/infrastructure/dto/HttpShippingMethodDto';
import { useShippingPageContext } from '@/Pages/ShippingPage/ShippingPageContext';
import useSetOnBehalf from '@/sections/cart/hooks/mutators/useSetOnBehalf';

import { getOtherGroups } from './../mappers/shippingMethodMapper';
import useGetGeneralShippingMethods from './useGetGeneralShippingMethods';

const useSetGeneralShippingMethods = () => {
  const { cart: cartRepository } = useRepository();
  const { data: cart } = useCart();
  const { results, shippingMethods } = useGetGeneralShippingMethods();
  const { getSkuHistory } = useShippingPageContext();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const onBehalf = useAppSelector((state) => state?.cart?.onBehalf);
  const { trigger: setBehalfTrigger } = useSetOnBehalf();

  const findOptionSelected = (
    item: ShippingItem
  ): {
    unmappedOption:
      | GeneralDeliveryShippingOptionDto
      | GeneralCollectShippingOptionDto
      | undefined;
    mappedOption: PickupOption | ShippingOption | undefined;
  } => {
    const group = shippingMethods?.deliveryGroups?.find(
      (group) => group?.id === item?.deliveryGroupId
    );

    const mappedOption = item?.isPickup
      ? group?.pickupOptions?.find((opt) => opt.id === item.id)
      : group?.shippingOptions?.find((opt) => opt.id === item.id);

    const deliveryGroup = results?.deliveryGroups?.find(
      (group) => group.id === item.deliveryGroupId
    );
    let option;
    if (deliveryGroup && mappedOption) {
      option = deliveryGroup?.shippingOptions?.find(
        (opt) =>
          opt.shippingMethod === mappedOption.shippingMethod &&
          opt.shippingType === mappedOption.shippingType &&
          opt.shippingSubType === mappedOption.shippingSubType
      );
    }

    return { unmappedOption: option, mappedOption: mappedOption };
  };

  const updateOnBehalfForDeliveryGroups = async (
    selectedShippingMethod: Cart
  ) => {
    const hasCollectOption = hasCollectShippingOption(selectedShippingMethod);
    if (!hasCollectOption) return;

    const hasOnBehalf = onBehalf?.length > 0;
    if (!hasOnBehalf) return;

    const onBehalfToBeUpdated = refreshOnBehalfGroups(
      onBehalf,
      selectedShippingMethod?.deliveryInfo?.deliveryGroups ?? []
    );

    onBehalfToBeUpdated.map(async (onBehalfItem) => {
      const deliveryGroupId = onBehalfItem?.deliveryGroupId ?? '';
      const authorizedPerson = {
        rut: onBehalfItem?.rut,
        fullname: onBehalfItem?.name
      };
      try {
        await setBehalfTrigger({
          deliveryGroupId: deliveryGroupId,
          authorizedPerson: authorizedPerson
        });
      } catch (error) {
        console.error(
          `Error setting on behalf for deliveryGroupId: ${deliveryGroupId}`,
          error
        );
      }
    });
  };

  const setDefaultShippingOptions = async (deliveryGroups: DeliveryGroup[]) => {
    try {
      const shippingMethodPayload: HttpShippingMethodDraft[] = deliveryGroups
        .map((group) => {
          if (group?.intangible) return;

          const deliveryGroup = results?.deliveryGroups?.find(
            (resultGroup) => resultGroup.id === group.id
          );
          const options = deliveryGroup?.shippingOptions;
          const selectedOption = options?.find((option) => option.selected);
          const shippingType =
            selectedOption?.shippingType as HttpShippingMethodDraft['shippingType'];
          const shippingMethod =
            selectedOption?.shippingMethod as HttpShippingMethodDraft['shippingMethod'];

          const isDelivery = shippingType === 'DELIVERY';

          const firstOption = selectedOption
            ?.options?.[0] as GeneralCollectOptionDto;

          const originNodeType = isDelivery
            ? selectedOption?.originNodeType
            : firstOption?.originNodeType;

          const originNodeCode = isDelivery
            ? selectedOption?.originNodeCode
            : firstOption?.originNodeCode;

          const skuHistory = getSkuHistory(deliveryGroup?.items?.[0]?.id ?? '');
          const clientPreferenceDate =
            skuHistory?.clientPreference?.[0]?.deliveryDateFrom;

          const foundByScheduledDate = selectedOption?.options?.find(
            (option) => {
              if (!clientPreferenceDate) return false;

              const classificationGroupMetaTimeSlot =
                group?.classificationGroupMeta?.timeSlotIndex;

              const isScheduledDate =
                option?.deliveryDateFrom?.startsWith(clientPreferenceDate);

              if (classificationGroupMetaTimeSlot) {
                const isSameTimeSlot = option?.timeSlots?.some(
                  (slot) => slot.name === classificationGroupMetaTimeSlot
                );
                return isScheduledDate && isSameTimeSlot;
              }

              return isScheduledDate;
            }
          );

          const option = foundByScheduledDate ?? firstOption;

          return {
            deliveryGroupId: group?.id,
            shippingType: shippingType,
            shippingSubType: selectedOption?.shippingSubType,
            shippingMethod: shippingMethod,
            option: {
              originNodeCode,
              originNodeType,
              shipDate: option?.shipDate as string,
              collectNodeCode:
                shippingType === 'COLLECT'
                  ? option?.collectNodeCode
                  : undefined,
              timeSlot: isDelivery ? option?.timeSlots?.[0] : undefined
            }
          };
        })
        .filter((item) => item !== undefined);

      const selectedShippingMethod = await selectShippingMethod(
        cartRepository,
        shippingMethodPayload
      );

      if (!selectedShippingMethod) {
        toast({
          title: 'Error al realizar el cambio',
          body: 'Por favor, intenta de nuevo.',
          position: 'bottom-left',
          status: 'error'
        });
        throw new Error('Error trying to select shipping method');
      }
      updateOnBehalfForDeliveryGroups(selectedShippingMethod);
    } catch (error) {
      handleSetError('Error trying to select shipping method');
    }
  };

  const setGeneralShippingOption = async (item: ShippingItem) => {
    try {
      setIsLoading(true);

      const selectedOptions = getOtherGroups(
        results as HttpShippingMethodDto,
        shippingMethods as ShippingMethod,
        item.deliveryGroupId as string,
        cart as Cart
      );

      const { unmappedOption, mappedOption } = findOptionSelected(item);
      if (unmappedOption && item.deliveryGroupId && cartRepository) {
        const { options } = unmappedOption;
        const optionTimeSlot = options[0].timeSlots
          ? options[0].timeSlots[0]
          : undefined;

        const payload: HttpShippingMethodDraft = {
          deliveryGroupId: item?.deliveryGroupId,
          shippingType: unmappedOption?.shippingType,
          shippingSubType: unmappedOption?.shippingSubType,
          shippingMethod: unmappedOption?.shippingMethod,
          option: {
            shipDate: mappedOption?.shipDate ?? '',
            originNodeType: mappedOption?.originNodeType ?? 'PROVIDER',
            originNodeCode: mappedOption?.originNodeCode ?? ''
          }
        };

        if (!item.isPickup) {
          payload.option.timeSlot = optionTimeSlot;
          selectedOptions.unshift(payload);
          const selectedShippingMethod = await selectShippingMethod(
            cartRepository,
            selectedOptions
          );
          if (!selectedShippingMethod) {
            setIsLoading(false);
            setError('Error trying to select shipping method');
            return false;
          }
        } else {
          const collectOption = mappedOption as PickupOption;
          payload.option.collectNodeCode = collectOption?.collectNodeCode;
          selectedOptions.unshift(payload);
          const selectedShippingMethod = await selectShippingMethod(
            cartRepository,
            selectedOptions
          );
          if (!selectedShippingMethod) {
            setIsLoading(false);
            setError('Error trying to select shipping method');
            return false;
          }
        }
      }
      setIsLoading(false);
      return true;
    } catch (error) {
      handleSetError('Error trying to select shipping method');
    }
  };

  const handleSetError = (value: string | undefined) => {
    setError(value);
  };

  return {
    setGeneralShippingOption,
    setDefaultShippingOptions,
    isLoading,
    error,
    handleSetError
  };
};

export default useSetGeneralShippingMethods;
